/**
 * Unified Product Filters Manager.
 *
 * Управляет фильтрами товаров в двух контекстах: catalog (AJAX-перезагрузка списка)
 * и modal (динамическая фильтрация подкатегорий по выбранным категориям).
 *
 * API:
 *   const m = new ProductFiltersManager({
 *       context: 'catalog'|'modal',
 *       filterId: 'product_list_filter',
 *       onFilterChange: filters => { ... },
 *       currentCategoryId: 0,
 *   });
 *   m.getSelectedFilters() → { categories, subcategories, materials, brands, tags }
 *   m.setFilters({ categories:[...], ... })
 *   m.reset()
 *   m.destroy()
 */

class ProductFiltersManager {
    constructor(config) {
        this.context = config.context || 'catalog';
        this.filterId = config.filterId || 'product_list_filter';
        this.onFilterChange = config.onFilterChange || (() => {});
        this.currentCategoryId = config.currentCategoryId || 0;
        this.container = document.getElementById(this.filterId);

        if (!this.container) {
            console.warn('Filter container not found:', this.filterId);
            return;
        }

        // Привязка listener-ов — сохраняем ссылки для destroy()
        this._onCaptionClick = this._onCaptionClick.bind(this);
        this._onCheckboxChange = this._onCheckboxChange.bind(this);
        this._onContainerClick = e => e.stopPropagation();
        this._onDocumentClick = this._onDocumentClick.bind(this);

        this.init();
    }

    init() {
        this.container.addEventListener('click', this._onCaptionClick);
        this.container.addEventListener('change', this._onCheckboxChange);
        this.container.addEventListener('click', this._onContainerClick);
        document.addEventListener('click', this._onDocumentClick);

        if (this.context === 'modal') {
            // Первичный прогон по подкатегориям
            this.filterSubcategoriesBySelectedCategories();
        }
    }

    _onCaptionClick(e) {
        const caption = e.target.closest('[data-js-filter-caption]');
        if (!caption || !this.container.contains(caption)) return;

        e.preventDefault();
        e.stopPropagation();

        const filterItem = caption.closest('[data-js-filter-item]');
        const currentId = filterItem?.getAttribute('data-js-filter-item');
        if (!filterItem) return;

        filterItem.classList.toggle('open');

        this.container.querySelectorAll('[data-js-filter-item]').forEach(item => {
            if (item.getAttribute('data-js-filter-item') !== currentId) {
                item.classList.remove('open');
            }
        });
    }

    _onCheckboxChange(e) {
        const checkbox = e.target;
        if (!checkbox.matches('input[type="checkbox"]')) return;

        if (this.context === 'modal' && checkbox.dataset.type === 'category') {
            this.filterSubcategoriesBySelectedCategories();
        }

        this.applyFilters();
    }

    _onDocumentClick() {
        this.container.querySelectorAll('[data-js-filter-item]').forEach(item => {
            item.classList.remove('open');
        });
    }

    filterSubcategoriesBySelectedCategories() {
        const selectedCategories = [];
        this.container.querySelectorAll('input[data-type="category"]:checked').forEach(cb => {
            selectedCategories.push(parseInt(cb.dataset.id, 10));
        });

        this.container.querySelectorAll('input[data-type="subcategory"]').forEach(cb => {
            const subcatCategoryId = parseInt(cb.dataset.categoryId, 10);
            const item = cb.closest('.product_list_filter_option');
            if (!item) return;

            if (selectedCategories.length === 0 || selectedCategories.includes(subcatCategoryId)) {
                item.style.display = '';
            } else {
                item.style.display = 'none';
                if (cb.checked) cb.checked = false;
            }
        });
    }

    getSelectedFilters() {
        const filters = {
            categories: [],
            subcategories: [],
            materials: [],
            brands: [],
            tags: [],
        };

        this.container.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => {
            const type = cb.dataset.type;
            const id = parseInt(cb.dataset.id, 10);
            if (Number.isNaN(id)) return;

            switch (type) {
                case 'category':    filters.categories.push(id); break;
                case 'subcategory': filters.subcategories.push(id); break;
                case 'material':    filters.materials.push(id); break;
                case 'brand':       filters.brands.push(id); break;
                case 'tag':         filters.tags.push(id); break;
                default:
                    // Универсальный fallback type+'s' если добавится новый тип
                    if (filters[`${type}s`]) filters[`${type}s`].push(id);
            }
        });

        return filters;
    }

    applyFilters() {
        this.onFilterChange(this.getSelectedFilters());
    }

    reset() {
        this.container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
            cb.checked = false;
        });
        if (this.context === 'modal') {
            this.container.querySelectorAll('.product_list_filter_option').forEach(el => {
                el.style.display = '';
            });
        }
        this.applyFilters();
    }

    setFilters(filters) {
        this.container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
            cb.checked = false;
        });
        Object.keys(filters).forEach(key => {
            const filterType = key.replace(/s$/, '');
            filters[key].forEach(id => {
                const cb = this.container.querySelector(
                    `input[data-type="${filterType}"][data-id="${id}"]`
                );
                if (cb) cb.checked = true;
            });
        });
        if (this.context === 'modal') {
            this.filterSubcategoriesBySelectedCategories();
        }
    }

    destroy() {
        if (!this.container) return;
        this.container.removeEventListener('click', this._onCaptionClick);
        this.container.removeEventListener('change', this._onCheckboxChange);
        this.container.removeEventListener('click', this._onContainerClick);
        document.removeEventListener('click', this._onDocumentClick);
    }
}

window.ProductFiltersManager = ProductFiltersManager;
